import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Users, Contact2, KanbanSquare, Calendar,
  Tags as TagsIcon, Settings, LogOut, Search, Sun, Moon, Menu, X, MapPin, CheckSquare, UserCog, Upload, BarChart3
} from "lucide-react";
import NotificationsBell from "./NotificationsBell";
import FeedbackButton from "./FeedbackButton";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/properties", label: "Immobili", icon: Building2, testid: "nav-properties" },
  { to: "/owners", label: "Proprietari", icon: Users, testid: "nav-owners" },
  { to: "/contacts", label: "Contatti", icon: Contact2, testid: "nav-contacts" },
  { to: "/investors", label: "Investitori", icon: UserCog, testid: "nav-investors" },
  { to: "/crm", label: "CRM", icon: KanbanSquare, testid: "nav-crm" },
  { to: "/tasks", label: "Attività", icon: CheckSquare, testid: "nav-tasks" },
  { to: "/map", label: "Mappa", icon: MapPin, testid: "nav-map" },
  { to: "/calendar", label: "Calendario", icon: Calendar, testid: "nav-calendar" },
  { to: "/tags", label: "Tag", icon: TagsIcon, testid: "nav-tags" },
  { to: "/imports", label: "Importa", icon: Upload, testid: "nav-imports" },
];

// Cliente ruolo: vede solo Opportunità (dashboard + archivio immobili). Nessun accesso a CRM/Anagrafiche.
const CLIENT_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/properties", label: "Opportunità", icon: Building2, testid: "nav-properties" },
];

const ADMIN_NAV = [{ to: "/admin/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" }];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = (user?.name || user?.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const isClient = user?.role === "client";
  const nav = isClient ? CLIENT_NAV : NAV;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className={`${mobileOpen ? "block" : "hidden"} lg:block fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[hsl(var(--sidebar))] border-r border-border/60 flex flex-col`} data-testid="sidebar">
        <div className="flex items-center justify-between px-6 h-16 border-b border-border/60">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" strokeWidth={2.2} />
            </div>
            <span className="font-heading font-semibold tracking-tight text-sm">LOOK OFF MARKET</span>
          </div>
          <button className="lg:hidden text-muted-foreground" onClick={() => setMobileOpen(false)} aria-label="Chiudi menu">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.to} to={item.to} end={item.end}
              onClick={() => setMobileOpen(false)}
              data-testid={item.testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
            >
              <item.icon className="w-4 h-4" strokeWidth={1.75} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border/60">
          <div className="text-xs text-muted-foreground px-3 pb-2 uppercase tracking-wider">Account</div>
          {user?.role === "admin" && ADMIN_NAV.map((item) => (
            <NavLink key={item.to} to={item.to} onClick={() => setMobileOpen(false)}
              data-testid={item.testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}>
              <item.icon className="w-4 h-4" strokeWidth={1.75} />{item.label}
            </NavLink>
          ))}
          {!isClient && (
            <NavLink to="/settings" onClick={() => setMobileOpen(false)}
              data-testid="nav-settings"
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}>
              <Settings className="w-4 h-4" strokeWidth={1.75} /> Impostazioni
            </NavLink>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 bg-background/80 backdrop-blur border-b border-border/60 flex items-center gap-3 px-4 md:px-6">
          <button className="lg:hidden text-muted-foreground" onClick={() => setMobileOpen(true)} aria-label="Apri menu" data-testid="mobile-menu-open">
            <Menu className="w-5 h-5" />
          </button>
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Cerca immobili, contatti, trattative…" className="pl-9 bg-muted/50 border-transparent focus-visible:bg-background" data-testid="global-search-input"
              onKeyDown={(e) => { if (e.key === "Enter" && e.currentTarget.value) navigate(`/properties?q=${encodeURIComponent(e.currentTarget.value)}`); }}
            />
          </div>
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-button" aria-label="Cambia tema">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          {!isClient && <NotificationsBell />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-md hover:bg-muted px-2 py-1 transition-colors" data-testid="user-menu-trigger">
                <Avatar className="w-8 h-8">
                  {user?.picture ? <AvatarImage src={user.picture} alt={user.name} /> : null}
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <div className="hidden md:flex flex-col text-left leading-tight">
                  <span className="text-sm font-medium">{user?.name || user?.email}</span>
                  <span className="text-xs text-muted-foreground capitalize">{user?.role}</span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm">{user?.name}</span>
                  <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {!isClient && (
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" /> Impostazioni
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} className="text-destructive focus:text-destructive" data-testid="menu-logout">
                <LogOut className="w-4 h-4 mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 min-w-0 p-4 md:p-8" data-testid="main-content">
          <Outlet />
        </main>
      </div>
      {!isClient && <FeedbackButton />}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    off_market: { label: "Off market", cls: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300" },
    pre_market: { label: "Pre-market", cls: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300" },
    on_market: { label: "On market", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" },
    in_negotiation: { label: "In trattativa", cls: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300" },
    sold: { label: "Venduto", cls: "bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-200" },
  };
  const c = map[status] || { label: status, cls: "bg-muted text-muted-foreground" };
  return <Badge className={`${c.cls} font-medium border-transparent`}>{c.label}</Badge>;
}
